<template>
  <div class="p-contact-edit-form">
    <div class="p-contact-edit-form__content">
      <contact-form
        ref="form"
        v-model="formValue"
        mode="edit"
        is-extended
        @status="handleFormStatusChange"
      >
        <contact-form-alert v-if="isAlertShown" />
      </contact-form>
    </div>
    <div class="p-divider" />
    <div class="p-contact-edit-form__footer">
      <el-button
        type="secondary"
        @click="handleCancelClick"
      >
        {{ $t('action.cancel') }}
      </el-button>
      <el-button
        native-type="submit"
        :loading="isContactEditing && !isVerificationEmailDialogShown"
        :disabled="isSaveDisabled"
        @click="handleSaveClick"
      >
        {{ $t('action.save') }}
      </el-button>
    </div>
    <verification-email-dialog
      :value="formValue"
      :original-email="originalEmail"
      :is-shown="isVerificationEmailDialogShown"
      @hide="hideVerificationEmailDialog"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import { areContactRequiredFieldsFilled } from '@/app/domain/Contact';
import { setAutoFocus } from '@/app/library/setAutoFocus';
import ContactForm from '@/app/components/ContactForm';
import ContactFormAlert from '@/app/components/ContactFormAlert';
import VerificationEmailDialog from '@/app/components/profile/VerificationEmailDialog';

export default {
  name: 'ContactEditForm',
  components: {
    ContactForm,
    ContactFormAlert,
    VerificationEmailDialog
  },
  inject: {
    isAlertShown: {
      from: 'isAlertShown',
      default: false
    }
  },
  props: {
    value: {
      type: Object,
      default: () => ({})
    }
  },
  emits: [
    'submit',
    'cancel'
  ],
  data() {
    return {
      formValue: {
        id: undefined,
        type: [],
        firstName: undefined,
        lastName: undefined,
        email: undefined,
        phone: '',
        jobTitle: undefined,
        userId: undefined
      },
      formStatus: {},
      originalEmail: '',
      isVerificationEmailDialogShown: false
    };
  },
  computed: {
    ...mapGetters('app', [
      'isContactEditing'
    ]),
    isSaveDisabled() {
      return !areContactRequiredFieldsFilled(this.formValue, true) || this.formStatus.invalid;
    }
  },
  created() {
    this.formValue = {
      ...this.formValue,
      ...this.value
    };
    this.originalEmail = this.value.email;
  },
  async mounted() {
    await this.$nextTick();
    setAutoFocus(this.$el);
  },
  methods: {
    ...mapActions('app', [
      'editContact'
    ]),
    handleFormStatusChange(status) {
      this.$set(this.formStatus, status.name, status.value);
    },
    async handleSaveClick() {
      const isFormValid = await this.$refs.form.validate();

      if (isFormValid) {
        if (this.isVerificationEmailRequired(this.formValue)) {
          this.showVerificationEmailDialog();
        } else {
          await this.editContact(this.formValue);
          this.$emit('submit');
        }
      }
    },
    handleCancelClick() {
      this.$emit('cancel');
    },
    isVerificationEmailRequired(value) {
      return value.userId && value.email !== this.originalEmail;
    },
    showVerificationEmailDialog() {
      this.isVerificationEmailDialogShown = true;
    },
    hideVerificationEmailDialog() {
      this.isVerificationEmailDialogShown = false;
    }
  }
};
</script>

<style lang="scss" scoped>
@import '@uikit/ui-kit/themes/acronis/src/common/variables';

.p-contact-edit-form {
  padding: 16px 24px;

  .p-divider {
    height: $av-border-width-sm;
    background-color: var(--av-brand-lighter);
    margin-left: -24px;
    margin-right: -24px;
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
    margin-top: 16px;
  }
}
</style>
